import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * CozyOnCozy cart drawer upsells.
 * Adds the chosen Cloud Socks variant to the cart and lets the theme's
 * cart-items-component re-render the drawer from the returned section HTML.
 *
 * Delegated listener on document so it survives the drawer being morphed.
 */
(() => {
  if (window.__cozyCartUpsellsBound) return;
  window.__cozyCartUpsellsBound = true;

  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.hasAttribute('data-cozy-variant')) return;
    const card = select.closest('[data-cozy-upsell]');
    const img = card?.querySelector('.cozy-upsell__img img');
    const src = select.selectedOptions[0]?.dataset.img;
    if (img instanceof HTMLImageElement && src) {
      img.src = src;
      img.removeAttribute('srcset');
    }
    // reset "Added" state if they change colour after adding
    if (card?.classList.contains('is-added')) {
      card.classList.remove('is-added');
      const btn = card.querySelector('[data-cozy-upsell-add]');
      btn?.removeAttribute('disabled');
      const label = btn?.querySelector('.cozy-upsell__btn-label');
      if (label) label.textContent = 'Add';
    }
  });

  document.addEventListener('click', async (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-cozy-upsell-add]') : null;
    if (!btn) return;

    const card = btn.closest('[data-cozy-upsell]');
    const wrap = btn.closest('[data-cozy-cart-extras]');
    if (!(card instanceof HTMLElement) || !(wrap instanceof HTMLElement)) return;

    const select = card.querySelector('select[data-cozy-variant]');
    const variantId = (select instanceof HTMLSelectElement && select.value) || card.dataset.variantId;
    const sectionId = wrap.dataset.sectionId;
    if (!variantId) return;

    // Collect every cart-items-component section on the page so all of them refresh
    const sections = new Set();
    if (sectionId) sections.add(sectionId);
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sections.add(el.dataset.sectionId);
    });

    card.classList.add('is-loading');
    btn.setAttribute('disabled', '');

    try {
      const body = JSON.stringify({
        items: [{ id: Number(variantId), quantity: Number(card.dataset.qty) || 1 }],
        sections: Array.from(sections).join(','),
        sections_url: window.location.pathname,
      });

      const res = await fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body }));
      const data = await res.json();

      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add to cart');
      }

      card.classList.remove('is-loading');
      card.classList.add('is-added');
      const label = btn.querySelector('.cozy-upsell__btn-label');
      if (label) label.textContent = 'Added';

      // Need the full cart for item_count; /cart/add.js returns only the added lines.
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

      document.dispatchEvent(
        new CartAddEvent(cart, 'cozy-cart-upsells', {
          source: 'cozy-cart-upsells',
          itemCount: cart.item_count,
          variantId: String(variantId),
          sections: data.sections,
        })
      );
    } catch (err) {
      console.error('[cozy-cart-upsells]', err);
      card.classList.remove('is-loading');
      btn.removeAttribute('disabled');
      const label = btn.querySelector('.cozy-upsell__btn-label');
      if (label) {
        label.textContent = 'Retry';
      }
    }
  });
})();
